import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit, Signal, WritableSignal, computed, effect, signal, untracked } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { get, partition, sortBy, uniqBy } from 'lodash';
import { Observable, skip } from 'rxjs';
import { map } from 'rxjs/operators';
import { TReducerState } from '../../app.config';
import { DynamicObservable } from '../../core/classes/dynamic-observable';
import { TreeBoardListComponent } from '../../layout/tree-board-list/tree-board-list.component';
import { BreadcrumbComponent, IBreadcrumbNode } from '../../shared/components/breadcrumb/breadcrumb.component';
import { ItemCardComponent } from '../../shared/components/item-card/item-card.component';
import { QuickItemGeneratorComponent } from '../../shared/components/quick-item-generator/quick-item-generator.component';
import { ELoadStatus } from '../../state/state.interface';
import { userActions } from '../../state/user/user.actions';
import { IPageConfiguration } from '../../state/user/user.interface';
import { HttpUtilitiesService } from '../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../app/base-crud-page.component';
import { ItemsKpiCardsStore } from '../items/items-kpi-cards/items-kpi-cards.store';
import { ItemsModalComponent } from '../items/items-modal/items-modal.component';
import { IBoard } from '../settings/boards/boards.interface';
import { EItemType } from '../settings/fields-field-sets/field-sets/field-sets.interface';
import { BoardCardComponent } from './board-card/board-card.component';
import { IHomeBoard, ISidebarBoard } from './home.interface';
import { HomeStore, IBoardComponentState } from './home.store';
import { MyItemsListStore } from './my-items-list/my-items-list-store.service';
import { MyItemsListComponent } from './my-items-list/my-items-list.component';

@Component({
  imports: [
    MatToolbar,
    BreadcrumbComponent,
    MatIcon,
    ReactiveFormsModule,
    MatDrawerContainer,
    MatDrawer,
    TreeBoardListComponent,
    NgForOf,
    AsyncPipe,
    QuickItemGeneratorComponent,
    TranslateModule,
    ItemCardComponent,
    BoardCardComponent,
    NgIf,
    MyItemsListComponent,
    RouterLink,
    NgClass,
  ],
  providers: [HomeStore, HttpUtilitiesService, ItemsKpiCardsStore],
  selector: 'app-home',
  styleUrl: './home.component.scss',
  templateUrl: './home.component.html',
})
export class HomeComponent extends BaseCrudPageComponent<IBoard, IBoardComponentState> implements OnInit {
  public readonly user = this.globalStore.selectSignal((state) => state.user.user);
  public readonly isCurrentUserLoadInProgress$ = this.globalStore.select(
    (state) =>
      state.user.getCurrentUserStatus === ELoadStatus.loading ||
      state.user.updateUserConfigurationsStatus === ELoadStatus.loading,
  );

  public readonly breadcrumbItems: WritableSignal<IBreadcrumbNode[]> = signal([]);
  public readonly shownBoards: Signal<IHomeBoard[]> = computed(() => {
    const boardsToSort = this.breadcrumbItems().length
      ? this.homeStore.data$()
      : uniqBy([...this.homeStore.data$(), ...this.homeStore.pinnedBoards()], 'id');

    return sortBy(boardsToSort, [(board) => !board.isPinned, (board) => board.name.toLowerCase()]).slice(0, 20);
  });

  public readonly shownPinnedSidebarBoards$ = this.homeStore.sidebarData$.pipe(
    map((data) =>
      sortBy(data.pinned, (pinnedContainer) =>
        pinnedContainer.boards.find((board) => board.parentId === null)?.name.toLowerCase(),
      ),
    ),
  );

  public readonly shownUnpinnedSidebarBoards$: Observable<ISidebarBoard[]> = this.homeStore.sidebarData$.pipe(
    map((sidebarData) => {
      const pinnedIds = sidebarData.pinned.map((pinnedContainer) => pinnedContainer.pinId);
      const outermostPinnedBoardIds = sidebarData.regular
        .filter((board) => board.parentId === null && pinnedIds.includes(board.id))
        .map((board) => board.id);

      return sortBy(
        sidebarData.regular.filter(
          (board) =>
            !outermostPinnedBoardIds.includes(board.id) &&
            !board.parents.some((parentId) => outermostPinnedBoardIds.includes(parentId)),
        ),
        (board) => board.name.toLowerCase(),
      );
    }),
  );

  public readonly numberOfMyIssuesItems = {
    [EItemType.issue]: 0,
    [EItemType.action]: 0,
  };

  public static readonly afterItemEditCompletedDynamic = new DynamicObservable<unknown>(null);

  public readonly EItemType = EItemType;
  public readonly maxNumberOfCards = MyItemsListStore.maxNumberOfCards;

  constructor(
    public readonly homeStore: HomeStore,
    public readonly itemsKpiCardsStore: ItemsKpiCardsStore,
    private readonly globalStore: Store<TReducerState>,
    private readonly router: Router,
  ) {
    super(homeStore, {
      ambiguousNumberContext: 'field.ambiguousNumberOfUser',
      multiContext: 'field.boards',
      nameProperty: 'name',
      singleContext: 'field.board',
    });

    effect(() => {
      this.user();
      this.homeStore.loadBoardsForDashboard(untracked(() => homeStore.lastLoadData()));

      if (untracked(() => !this.breadcrumbItems().length)) {
        this.homeStore.loadPinnedBoardsForDashboard();
      }
    });
  }

  public ngOnInit(): void {
    HomeComponent.afterItemEditCompletedDynamic.obs$.pipe(skip(1)).subscribe(() => {
      this.refreshListedItems();
      this.refreshNumberOfIssueAndActions();
      this.homeStore.loadBoardsForDashboard(this.homeStore.lastLoadData());
      this.homeStore.loadPinnedBoardsForDashboard();
    });

    this.refreshListedItems();
    this.refreshNumberOfIssueAndActions();
    this.homeStore
      .select((state) => state.lastLoadData)
      .subscribe((lastLoadData) => {
        if (lastLoadData.id === null) {
          this.breadcrumbItems.set([]);
        }

        const isBreadcrumbItemAlreadyIn = this.breadcrumbItems().some((item) => item.id === lastLoadData.id);

        if (lastLoadData.breadcrumbUpdate && !isBreadcrumbItemAlreadyIn) {
          this.breadcrumbItems.set([...this.breadcrumbItems(), lastLoadData.breadcrumbUpdate]);
        }
      });
  }

  public getBoardsOfBranch(id?: number): void {
    if (!id) {
      this.homeStore.loadPinnedBoardsForDashboard();
      this.homeStore.loadBoardsForDashboard({ breadcrumbUpdate: undefined, id: null });

      return;
    }

    const item: IHomeBoard | undefined = this.shownBoards().find((board: IHomeBoard) => board.id === id);

    this.homeStore.loadBoardsForDashboard({ breadcrumbUpdate: item, id });
  }

  public togglePin(boardId: number, isCurrentlyPinned: boolean): void {
    const componentName = 'HomeComponent';

    const pageConfiguration: IPageConfiguration['HomeComponent'] = get(
      this.user(),
      `pageConfiguration.${componentName}`,
      [],
    );

    const [pinnedBoardsConfigurations, restOfTheHomeConfigurations]: [
      IPageConfiguration['HomeComponent'],
      IPageConfiguration['HomeComponent'],
    ] = partition(pageConfiguration, (configuration) => configuration.name === 'pinnedBoards');

    const pinnedBoardsConfiguration = pinnedBoardsConfigurations[0] ?? { name: 'pinnedBoards', value: [] };

    const updatedPinnedList = isCurrentlyPinned
      ? pinnedBoardsConfiguration?.value.filter((value) => value !== boardId)
      : [...pinnedBoardsConfiguration?.value, boardId];

    this.globalStore.dispatch(
      userActions.updateUserConfigurations({
        pageConfiguration: {
          ...(this.user()!.pageConfiguration ?? {}),
          [componentName]: [
            ...restOfTheHomeConfigurations,
            {
              ...pinnedBoardsConfiguration,
              value: updatedPinnedList,
            },
          ],
        },
      }),
    );
  }

  public navigateToBoard(boardKey: string): void {
    this.router.navigate([`/items/${boardKey}/all`]);
  }

  public refreshListedItems(): void {
    this.homeStore.loadQuickItems({
      fields: ['name', 'key', 'createdAt', 'assignee||name', 'itemType', 'dueDate', 'strikeCount'],
      filters: [{ field: 'isQuick', ids: [true] }],
      join: ['assignee||name', 'boardItemConfiguration||itemType,itemCategory'],
      limit: 20,
      sort: [{ active: 'createdAt', direction: 'desc' }],
    });
  }

  public refreshNumberOfIssueAndActions(): void {
    this.itemsKpiCardsStore.loadItems({
      filters: [{ field: 'assigneeId', ids: [this.user()!.id] }],
    });
  }

  public openItemEditModal(itemId: number): void {
    HomeComponent.afterItemEditCompletedDynamic.switch(
      this.matDialog
        .open(ItemsModalComponent, {
          ...ItemsModalComponent.modalParameters,
          data: {
            idForEdit: itemId,
            submitButtonText: this.saveChanges,
          },
          restoreFocus: false,
        })
        .afterClosed(),
    );
  }
}
